import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiBookOpen, FiChevronDown, FiInfo, FiMinus, FiPlus, FiSearch, FiShoppingCart, FiSliders } from 'react-icons/fi';
import { SORT_OPTIONS, formatMoney, getSeasonLabel, sortCourses } from './courseCatalogHelpers';
import CourseCard from './courseCatalog/CourseCard';
import useCourseCatalog from './useCourseCatalog';
import logo from '../../assets/logo.png';
import styles from './CourseCatalog.module.css';

const CONTACT_EMAIL = 'admin@faithinsight.org';

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function CourseCatalog() {
  const {
    isAuthenticated,
    checkoutNotice,
    showRedirectMessage,
    loading,
    error,
    searchTerm, setSearchTerm,
    sortBy, setSortBy,
    filteredCourses,
    activeCourses,
    previousCourses,
    allActiveCourses,
    filteredProducts,
    selectedCourses,
    selectedProducts,
    expandedDescriptions,
    bulkPricing,
    bundleOffer,
    addBundleToCart,
    removeBundleFromCart,
    isEnrolledInCourse,
    toggleCourseSelection,
    toggleProductSelection,
    toggleCourseDescription,
    getTotalPrice,
    getOriginalTotal,
    handleMultiEnroll,
  } = useCourseCatalog();

  // Sections only exist after the data loads, so /portal/catalog#sponsorship is scrolled to here, not by the browser.
  useEffect(() => {
    const id = loading ? '' : decodeURIComponent((window.location.hash || '').replace(/^#/, ''));
    const target = id && document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading]);

  const cartCount = selectedCourses.length + selectedProducts.length;
  const hasSelectedItems = cartCount > 0;
  const upcomingSeason = allActiveCourses.length > 0 ? getSeasonLabel(sortCourses(allActiveCourses, SORT_OPTIONS.oldest)[0]) : '';
  const bundleInCart = Boolean(bundleOffer) && bundleOffer.courses.every(
    (course) => selectedCourses.some((selected) => selected.id === course.id)
  );

  const renderCourseCard = (course, variant, index) => (
    <CourseCard
      key={course.id}
      course={course}
      variant={variant}
      index={index}
      bundleOffer={bundleOffer}
      isSelected={selectedCourses.some(selected => selected.id === course.id)}
      isEnrolled={isEnrolledInCourse(course.id)}
      isExpanded={Boolean(expandedDescriptions[`course-${course.id}`])}
      onToggleSelection={toggleCourseSelection}
      onToggleExpansion={toggleCourseDescription}
    />
  );

  return (
    <>
      <div className={`${styles.container} ${hasSelectedItems ? styles.hasSelectedItems : ''}`}>
        {/* Signed-in students already have the app's own header (with the logo) above this page. */}
        {!isAuthenticated && (
          <header className={styles.topBar}>
            <div className={`${styles.inner} ${styles.topBarInner}`}>
              {/* The main Faith Insight website (outside the portal), so a plain link rather than a router Link. */}
              <a href="https://www.faithinsight.org" className={styles.brand}>
                <img src={logo} alt="" className={styles.brandLogo} />
                <span className={styles.brandName}>Faith Insight</span>
              </a>
              <div className={styles.topBarActions}>
                <Link to="/login?redirect=%2Fcatalog" className={styles.topBarLink}>Sign in</Link>
                <button
                  type="button"
                  className={styles.cartButton}
                  onClick={handleMultiEnroll}
                  disabled={!hasSelectedItems}
                  aria-label={`Checkout, ${cartCount} ${cartCount === 1 ? 'item' : 'items'} in cart`}
                >
                  <FiShoppingCart size={15} aria-hidden="true" />
                  Cart · {cartCount}
                </button>
              </div>
            </div>
          </header>
        )}

        <section className={styles.hero}>
          <div className={`${styles.inner} ${styles.heroInner}`}>
            <div className={styles.heroText}>
              {upcomingSeason && (
                <span className={styles.eyebrow}>{upcomingSeason} enrollment is open</span>
              )}
              <h1 className={styles.heroTitle}>
                Explore. Learn. <span className={styles.heroAccent}>Grow in Faith.</span>
              </h1>
              <p className={styles.heroSubtitle}>
                Explore structured, instructor-led courses designed to strengthen your Islamic knowledge, deepen your faith, and support your journey of lifelong learning. All course fees are donated directly to ICN.
              </p>
              <div className={styles.heroActions}>
                <button type="button" className={styles.heroPrimary} onClick={() => scrollToSection('active-courses')}>
                  Browse active courses
                  <FiArrowRight size={15} aria-hidden="true" />
                </button>
                {filteredProducts.length > 0 && (
                  <button type="button" className={styles.heroSecondary} onClick={() => scrollToSection('sponsorship')}>
                    Sponsor a student
                  </button>
                )}
              </div>
            </div>

            {bundleOffer && (
              <aside className={styles.bundleCard} aria-label="Bundle offer">
                <div className={styles.bundleTop}>
                  <span className={styles.bundleLabel}>Bundle offer</span>
                  <span className={styles.savePill}>Save {formatMoney(bundleOffer.discount)}</span>
                </div>
                <h2 className={styles.bundleTitle}>
                  Enroll in any 2 active courses and get {formatMoney(bundleOffer.discount)} off
                </h2>
                <div className={styles.bundleBreakdown}>
                  {bundleOffer.courses.map((course) => (
                    <div key={course.id} className={styles.bundleRow}>
                      <span>{course.name}</span>
                      <span>{formatMoney(course.price)}</span>
                    </div>
                  ))}
                  <div className={styles.bundleTotal}>
                    <span>Bundle total</span>
                    <span>
                      <s className={styles.bundleWas}>{formatMoney(bundleOffer.total)}</s>
                      <strong className={styles.bundleNow}>{formatMoney(bundleOffer.finalPrice)}</strong>
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className={`${styles.bundleButton} ${bundleInCart ? styles.bundleButtonRemove : ''}`}
                  onClick={() => (bundleInCart ? removeBundleFromCart(bundleOffer) : addBundleToCart(bundleOffer))}
                >
                  {bundleInCart ? <FiMinus size={15} aria-hidden="true" /> : <FiPlus size={15} aria-hidden="true" />}
                  {bundleInCart ? 'Remove both from cart' : 'Add both to cart'}
                </button>
                <p className={styles.bundleNote}>Applied automatically at checkout · active courses only</p>
              </aside>
            )}
          </div>
        </section>

        <div className={styles.inner}>
          <div className={styles.filterCard}>
            <div className={styles.searchContainer}>
              <FiSearch className={styles.searchIcon} size={17} aria-hidden="true" />
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search courses, topics or instructors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <label className={styles.sortContainer}>
              <FiSliders className={styles.sortIcon} size={18} aria-hidden="true" />
              <span className={styles.sortLabel}>Sort</span>
              <select
                className={styles.sortSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort courses"
              >
                <option value={SORT_OPTIONS.newest}>Newest first</option>
                <option value={SORT_OPTIONS.oldest}>Oldest first</option>
                <option value={SORT_OPTIONS.nameAsc}>A - Z</option>
                <option value={SORT_OPTIONS.nameDesc}>Z - A</option>
              </select>
              <FiChevronDown className={styles.sortChevron} size={14} aria-hidden="true" />
            </label>
          </div>

          {/* The hero and search bar need no fetched data and always render immediately; only this
              content area swaps between spinner, error and the lists. */}
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
            </div>
          ) : error ? (
            <div className={styles.errorMessage}>{error}</div>
          ) : (
            <>
              {showRedirectMessage && (
                <motion.div
                  className={styles.redirectMessage}
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className={styles.messageIcon}>
                    <FiInfo size={22} />
                  </div>
                  <p className={styles.messageText}>
                    Choose your courses and proceed to checkout to complete your registration.
                  </p>
                </motion.div>
              )}

              {filteredCourses.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <FiBookOpen />
                  </div>
                  <h3>No courses found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                <div className={styles.courseGroups}>
                  {activeCourses.length > 0 && (
                    <section className={styles.courseGroup} id="active-courses" aria-labelledby="active-courses-heading">
                      <div className={styles.courseGroupHeader}>
                        <div>
                          <span className={styles.sectionEyebrow}>
                            <span className={styles.liveDot} aria-hidden="true" />
                            Enrolling now
                          </span>
                          <h2 id="active-courses-heading" className={styles.courseGroupTitle}>Active courses</h2>
                        </div>
                        <span className={styles.courseGroupCount}>
                          {activeCourses.length} {activeCourses.length === 1 ? 'course' : 'courses'}
                        </span>
                      </div>
                      <div className={styles.activeGrid}>
                        {activeCourses.map((course, index) => renderCourseCard(course, 'active', index))}
                      </div>
                    </section>
                  )}

                  {previousCourses.length > 0 && (
                    <section className={styles.courseGroup} id="previous-courses" aria-labelledby="previous-courses-heading">
                      <div className={styles.courseGroupHeader}>
                        <div>
                          <span className={`${styles.sectionEyebrow} ${styles.sectionEyebrowGold}`}>Past sessions</span>
                          <h2 id="previous-courses-heading" className={styles.courseGroupTitle}>Previous courses</h2>
                        </div>
                        <span className={styles.courseGroupCount}>
                          Enroll Anytime · Access course materials &amp; recordings · {previousCourses.length} {previousCourses.length === 1 ? 'course' : 'courses'}
                        </span>
                      </div>
                      <div className={styles.previousGrid}>
                        {previousCourses.map((course, index) => renderCourseCard(course, 'previous', index))}
                      </div>
                    </section>
                  )}
                </div>
              )}

              {filteredProducts.length > 0 && (
                <section className={styles.sponsorBand} id="sponsorship" aria-labelledby="sponsorship-heading">
                  <div className={styles.sponsorText}>
                    <span className={`${styles.sectionEyebrow} ${styles.sectionEyebrowGold}`}>Sadaqah · Give knowledge</span>
                    <h2 id="sponsorship-heading" className={styles.sponsorTitle}>Sponsor a student</h2>
                    <p className={styles.sponsorCopy}>
                      Cover the tuition for a student who wants to learn. Choose an option to add it to your cart.
                    </p>
                  </div>
                  <div className={styles.sponsorOptions}>
                    {filteredProducts.map((product) => {
                      const isSelected = selectedProducts.some(selected => selected.id === product.id);
                      const featured = product.product_type === 'curriculum_package';
                      return (
                        <motion.div
                          key={product.id}
                          className={`${styles.sponsorOption} ${featured ? styles.sponsorOptionFeatured : ''} ${isSelected ? styles.selected : ''}`}
                          initial={false}
                        >
                          <span className={styles.sponsorOptionName}>{product.name}</span>
                          {product.description && (
                            <span className={styles.sponsorOptionDesc}>{product.description}</span>
                          )}
                          <span className={styles.sponsorOptionPrice}>
                            {formatMoney(product.price ? parseFloat(product.price) : 0)}
                          </span>
                          <button
                            type="button"
                            className={styles.sponsorButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleProductSelection(product);
                            }}
                          >
                            {isSelected ? <FiMinus size={15} /> : <FiPlus size={15} />}
                            {isSelected ? 'Remove from Cart' : 'Add to Cart'}
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}

          <footer className={styles.pageFooter}>
            <span>© {new Date().getFullYear()} Faith Insight</span>
            <span>
              Questions? <a href={`mailto:${CONTACT_EMAIL}`}>Contact Us</a>
            </span>
          </footer>
        </div>
      </div>

      {/* Sticky Checkout Bar - Only show when items are selected */}
      {hasSelectedItems && (
        <div className={styles.multiSelectControls}>
          {checkoutNotice && <div className={styles.errorMessage} role="alert">{checkoutNotice}</div>}
          <div className={styles.cartSummary}>
            <div className={styles.cartInfo}>
              <div className={styles.cartCount}>
                {cartCount} item{cartCount !== 1 ? 's' : ''} selected
                {selectedCourses.length > 0 && ` (${selectedCourses.length} course${selectedCourses.length !== 1 ? 's' : ''})`}
                {selectedProducts.length > 0 && ` (${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''})`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }}>
                {bulkPricing && bulkPricing.pricing && bulkPricing.pricing.discountAmount > 0 && (
                  <>
                    <span className={styles.cartOriginalTotal}>${getOriginalTotal().toFixed(2)}</span>
                    <span className={styles.cartDiscount}>
                      (Save ${bulkPricing.pricing.discountAmount.toFixed(2)})
                    </span>
                  </>
                )}
                <span className={styles.cartTotal}>Total: ${getTotalPrice().toFixed(2)}</span>
              </div>
            </div>
            <button
              className={styles.enrollAllButton}
              onClick={handleMultiEnroll}
              disabled={!hasSelectedItems}
            >
              <FiShoppingCart size={16} />
              Checkout
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CourseCatalog;
